import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reminderPreferencesAPI, resourceEmailAPI } from '../services/api';
import { EmailIcon } from '../utils/icons';

const COOLDOWN_STORAGE_PREFIX = 'resource-email-cooldown:';

const readCooldownRemaining = (resourceKey: string): number => {
  try {
    const raw = sessionStorage.getItem(`${COOLDOWN_STORAGE_PREFIX}${resourceKey}`);
    if (!raw) return 0;
    const remaining = Math.ceil((Number(raw) - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  } catch {
    return 0;
  }
};

const writeCooldown = (resourceKey: string, seconds: number): void => {
  try {
    sessionStorage.setItem(
      `${COOLDOWN_STORAGE_PREFIX}${resourceKey}`,
      String(Date.now() + seconds * 1000)
    );
  } catch {
    // ignore storage errors
  }
};

const autofillSx = {
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
    WebkitBoxShadow: '0 0 0 1000px #fff inset',
    boxShadow: '0 0 0 1000px #fff inset',
    WebkitTextFillColor: '#333',
    caretColor: '#333',
    transition: 'background-color 5000s ease-in-out 0s',
  },
};

type Props = {
  courseId: string;
  chapterId: string;
  itemId: string;
  moduleId: string;
  knownEmail: string | null;
  onEmailSaved?: (email: string) => void;
};

const EmailResourceButton: React.FC<Props> = ({
  courseId,
  chapterId,
  itemId,
  moduleId,
  knownEmail,
  onEmailSaved,
}) => {
  const { t } = useTranslation();
  const resourceKey = `${courseId}:${itemId}:${moduleId}`;
  const [cooldown, setCooldown] = useState(() => readCooldownRemaining(resourceKey));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCooldown((current) => {
        const next = Math.max(0, current - 1);
        return next;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const applyCooldown = (seconds: number) => {
    writeCooldown(resourceKey, seconds);
    setCooldown(seconds);
  };

  const mapSendError = (err: unknown): string => {
    if (axios.isAxiosError(err)) {
      const data = err.response?.data as
        | { code?: string; message?: string; retryAfterSeconds?: number }
        | undefined;
      if (data?.code === 'COOLDOWN') {
        applyCooldown(data.retryAfterSeconds || 60);
        return t('capsule.emailResourceCooldown', {
          seconds: data.retryAfterSeconds || 60,
        });
      }
      if (data?.code === 'FILE_TOO_LARGE') {
        return t('capsule.emailResourceTooLarge');
      }
      if (data?.code === 'EMAIL_REQUIRED') {
        return t('reminderEmails.emailLabel');
      }
      if (err.response?.status === 409 || data?.message?.includes('déjà utilisée')) {
        return t('capsule.emailResourceEmailInUse');
      }
      if (data?.message && (err.response?.status === 400 || err.response?.status === 413)) {
        return data.message;
      }
    }
    return t('capsule.emailResourceError');
  };

  const sendResource = async (payload?: { email: string; emailConfirm: string }) => {
    setSending(true);
    setError(null);
    try {
      const result = await resourceEmailAPI.send(courseId, {
        chapterId,
        itemId,
        moduleId,
        email: payload?.email,
        emailConfirm: payload?.emailConfirm,
      });
      applyCooldown(result.cooldownSeconds || 60);
      setSuccessEmail(result.email);
      setDialogOpen(false);
      setConfirmOpen(false);
      setConfirmEmail(null);
      setEmail('');
      setEmailConfirm('');
      if ((payload?.email || result.email) && onEmailSaved) {
        onEmailSaved(result.email);
      }
    } catch (err) {
      setError(mapSendError(err));
    } finally {
      setSending(false);
    }
  };

  const handleClick = async () => {
    if (cooldown > 0 || sending) return;
    setError(null);

    let recipient = knownEmail;
    if (!recipient) {
      try {
        const prefs = await reminderPreferencesAPI.getGlobal();
        recipient = prefs.email;
        if (prefs.email && onEmailSaved) {
          onEmailSaved(prefs.email);
        }
      } catch {
        recipient = null;
      }
    }

    if (!recipient) {
      setDialogOpen(true);
      return;
    }

    setConfirmEmail(recipient);
    setConfirmOpen(true);
  };

  const handleDialogSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (email.trim().toLowerCase() !== emailConfirm.trim().toLowerCase()) {
      setError(t('reminderEmails.emailMismatch'));
      return;
    }
    await sendResource({ email: email.trim(), emailConfirm: emailConfirm.trim() });
  };

  const tooltip =
    cooldown > 0
      ? t('capsule.emailResourceCooldown', { seconds: cooldown })
      : t('capsule.emailResourceTooltip');

  return (
    <>
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            className="capsule__module-file-download capsule__module-file-email"
            onClick={() => void handleClick()}
            disabled={sending || cooldown > 0}
            aria-label={t('capsule.emailResourceTooltip')}
          >
            {sending ? (
              <CircularProgress size={18} sx={{ color: '#fff' }} />
            ) : (
              <EmailIcon size={20} color="#fff" />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <Dialog
        open={dialogOpen}
        onClose={() => !sending && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleDialogSubmit}>
          <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3 }}>
            {t('capsule.emailResourceDialogTitle')}
          </DialogTitle>
          <DialogContent>
            <p style={{ marginTop: 0 }}>
              {t('capsule.emailResourceDialogDescription')}
            </p>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              type="email"
              margin="dense"
              required
              autoComplete="off"
              label={t('reminderEmails.emailLabel')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={autofillSx}
            />
            <TextField
              fullWidth
              type="email"
              margin="dense"
              required
              autoComplete="off"
              label={t('reminderEmails.emailConfirmLabel')}
              value={emailConfirm}
              onChange={(e) => setEmailConfirm(e.target.value)}
              sx={autofillSx}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={sending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="contained" disabled={sending}>
              {sending ? t('capsule.emailResourceSending') : t('capsule.emailResourceSend')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={() => !sending && setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3 }}>
          {t('capsule.emailResourceDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <p style={{ marginTop: 0 }}>
            {t('capsule.emailResourceConfirmDescription', { email: confirmEmail || '' })}
          </p>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={sending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={sending}
            onClick={() => void sendResource()}
          >
            {sending ? t('capsule.emailResourceSending') : t('capsule.emailResourceSend')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successEmail) || (Boolean(error) && !dialogOpen && !confirmOpen)}
        autoHideDuration={4000}
        onClose={() => {
          setSuccessEmail(null);
          if (!dialogOpen && !confirmOpen) setError(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={successEmail ? 'success' : 'error'}
          onClose={() => {
            setSuccessEmail(null);
            if (!dialogOpen && !confirmOpen) setError(null);
          }}
        >
          {successEmail
            ? t('capsule.emailResourceSent', { email: successEmail })
            : error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmailResourceButton;
